import { inject, Injectable } from '@angular/core';
import { EnvironmentService } from '@kanzi-apes/kanzi-utils';
import { KanziRestClientService } from '@kanzi-apes/kanzi-rest-api';
import { map, Observable } from 'rxjs';
import {
  ListResults,
  LocationsWMSFilterModel,
  LocationTypesModel,
  WarehouseListModel,
  WarehouseListV3Model,
} from '@kanzi-apes/kanzi-models';
import { ILocationWmsCreate } from '../models/locations-wms/locations-create.interface';
import { getUrlPresignedFilter, ILocationWMS, IUrlPresignedResults } from '../models/locations-wms/locations.interface';
import { IPositionWms } from '../models/locations-wms/zone-position.interface';
import { IPositionCreate } from '../models/locations-wms/zone-position-create.interface';

@Injectable({
  providedIn: 'root',
})
export class KanziLocationsService {
  private httpClient = inject(KanziRestClientService);
  private envService = inject(EnvironmentService);
  private locationsWMSUrl = `${this.envService.wmsEnvironment?.apiRestModules.kongWMSAPI?.locations.apiURL}`;
  private locationsWMSPath = `${this.envService.wmsEnvironment?.apiRestModules.kongWMSAPI?.locations.apiPath}`;
  private locationsV3Url = `${this.envService.wmsEnvironment?.apiRestModules.kongWMSAPI?.locationsV3.apiURL}`;
  private locationsV3Path = `${this.envService.wmsEnvironment?.apiRestModules.kongWMSAPI?.locationsV3.apiPath}`;
  private typeLocationsUrl = `${this.envService.wmsEnvironment?.apiRestModules.kongWMSAPI?.locationTypes.apiURL}`;
  private typeLocationsPath = `${this.envService.wmsEnvironment?.apiRestModules.kongWMSAPI?.locationTypes.apiPath}`; 
  public getWarehouseList(
    filters: LocationsWMSFilterModel
  ): Observable<WarehouseListModel> {
    const url = `${this.locationsWMSUrl}${this.locationsWMSPath}`;
    return this.httpClient.get(
      url,
      filters,
      'Error al listar las bodegas',
      {},
      { tokenName: 'KanziWMSUserToken', tokenPrefix: 'COGNITO' }
    );
  }

  /**
   * Retrieves the warehouse list using the V3 locations endpoint.
   * @param filters - Filters to apply to the warehouse query
   * @returns Observable with the paginated warehouse list (V3 pagination format)
   */
  public getWarehouseListV3(
    filters: LocationsWMSFilterModel
  ): Observable<WarehouseListV3Model> {
    const url = `${this.locationsV3Url}${this.locationsV3Path}`;
    return this.httpClient.get(
      url,
      filters,
      'Error al listar las bodegas',
      {},
      { tokenName: 'KanziWMSUserToken', tokenPrefix: 'COGNITO' }
    );
  }
  public getWarehouseById(filters: LocationsWMSFilterModel): Observable<WarehouseListModel> {
    const url = `${this.locationsWMSUrl}${this.locationsWMSPath}`;
    return this.httpClient.get(
      url,
      filters,
      'Error al obtener la bodega',
      {},
      { tokenName: 'KanziWMSUserToken', tokenPrefix: 'COGNITO' }
    );
  }

  public getZones(
    filters: LocationsWMSFilterModel
  ): Observable<WarehouseListModel> {
    const url = `${this.locationsWMSUrl}${this.locationsWMSPath}`;
    return this.httpClient.get(
      url,
      filters,
      'Error al listar las zonas',
      {},
      { tokenName: 'KanziWMSUserToken', tokenPrefix: 'COGNITO' }
    );
  }

  /**
   * Retrieves zones using the V3 locations endpoint with offset/limit pagination.
   * @param filters - Filters to apply including offset and limit
   * @returns Observable with paginated zone list in V3 format
   */
  public getZonesV3(
    filters: LocationsWMSFilterModel
  ): Observable<WarehouseListV3Model> {
    const url = `${this.locationsV3Url}${this.locationsV3Path}`;
    return this.httpClient.get(
      url,
      filters,
      'Error al listar las zonas',
      {},
      { tokenName: 'KanziWMSUserToken', tokenPrefix: 'COGNITO' }
    );
  }

  /**
   * Retrieves positions using the V3 locations endpoint with offset/limit pagination.
   * @param filters - Filters to apply including offset and limit
   * @returns Observable with paginated position list in V3 format
   */
  public getPositionsV3(
    filters: LocationsWMSFilterModel
  ): Observable<WarehouseListV3Model> {
    const url = `${this.locationsV3Url}${this.locationsV3Path}`;
    return this.httpClient.get(
      url,
      filters,
      'Error al listar las ubicaciones',
      {},
      { tokenName: 'KanziWMSUserToken', tokenPrefix: 'COGNITO' }
    );
  }

  public getLocationByParent(filters: LocationsWMSFilterModel): Observable<WarehouseListModel> {
    const url = `${this.locationsWMSUrl}${this.locationsWMSPath}`;
    return this.httpClient.get(
      url,
      filters,
      'Error al obtener la locación',
      {},
      { tokenName: 'KanziWMSUserToken', tokenPrefix: 'COGNITO' }
    );
  }

  /**
   * Retrieves child locations by parent using the V3 endpoint with offset/limit pagination.
   * @param filters - Filters including parent_id__in, limit, and offset
   * @returns Observable with paginated location list in V3 format
   */
  public getLocationByParentV3(filters: LocationsWMSFilterModel): Observable<WarehouseListV3Model> {
    const url = `${this.locationsV3Url}${this.locationsV3Path}`;
    return this.httpClient.get(
      url,
      filters,
      'Error al obtener la locación',
      {},
      { tokenName: 'KanziWMSUserToken', tokenPrefix: 'COGNITO' }
    );
  }
  public getURLPresigned(filters: getUrlPresignedFilter): Observable<IUrlPresignedResults> {
    const url = `${this.locationsWMSUrl}${this.locationsWMSPath}/put_presigned_url`;
    return this.httpClient.get(
      url,
      filters,
      'Error al obtener la url',
      {},
      { tokenName: 'KanziWMSUserToken', tokenPrefix: 'COGNITO' }
    );
  }

  public getZonePositionById(filters: LocationsWMSFilterModel): Observable<ListResults<IPositionWms>> {
    const url = `${this.locationsWMSUrl}${this.locationsWMSPath}`;
    return this.httpClient.get(
      url,
      filters,
      'Error al obtener la locación',
      {},
      { tokenName: 'KanziWMSUserToken', tokenPrefix: 'COGNITO' }
    );
  }

  public getLocationTypes(): Observable<LocationTypesModel> {
    const url = `${this.typeLocationsUrl}${this.typeLocationsPath}/groups/types`;
    return this.httpClient.get( 
      url,
      {},
      'Error al obtener los tipos de bodegas',
      {},
      { tokenName: 'KanziWMSUserToken', tokenPrefix: 'COGNITO' }
    );
  }

  public createWarehouse(body: ILocationWmsCreate): Observable<ILocationWMS> {
    const url = `${this.locationsWMSUrl}${this.locationsWMSPath}`;
    return this.httpClient.post(
      url,
      body,
      'Error al crear la bodega.',
      {},
      { tokenName: 'KanziWMSUserToken', tokenPrefix: 'COGNITO' }
    );
  }

  public updateWarehouse(body: ILocationWmsCreate): Observable<ILocationWMS> {
    const  {id, ...rest} = body
    const url = `${this.locationsWMSUrl}${this.locationsWMSPath}/${id}`;
    return this.httpClient.patch(
      url,
      rest,
      {},
      'Error al crear la bodega.',
      {},
      { tokenName: 'KanziWMSUserToken', tokenPrefix: 'COGNITO' }
    );
  }

  public createZonePosition(body: IPositionCreate): Observable<IPositionWms> {
    const url = `${this.locationsWMSUrl}${this.locationsWMSPath}`;
    return this.httpClient.post(
      url,
      body,
      'Error al crear la locación.',
      {},
      { tokenName: 'KanziWMSUserToken', tokenPrefix: 'COGNITO' }
    );
  }

  public updateZonePosition(body: IPositionCreate): Observable<IPositionWms> {
    const  {id, ...rest} = body
    const url = `${this.locationsWMSUrl}${this.locationsWMSPath}/${id}`;
    return this.httpClient.patch(
      url,
      rest,
      {},
      'Error al actualizar la locación.',
      {},
      { tokenName: 'KanziWMSUserToken', tokenPrefix: 'COGNITO' }
    );
  }
}
